import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { FormGroup, Validators, FormControl } from '@angular/forms';

import { BehaviorSubject, Subscription, tap, combineLatest, Subject, filter, map, Observable, throwError } from 'rxjs';

import { MessageService } from '@shared/services/message-service';

import { ActivityBlockingService } from '@shared/services/activity-blocking.service';
import { SortActivity } from '@shared/models/sort-activity';
import { ShiftStatus } from '@shared/models/worked-shift';

import { ProductService } from '../../../../services/product.service';
import { InventoryService } from '../../../../services/inventory.service';
import { ShiftContextService } from '../../../../services/shift-context.service';
import { saveDebounce } from '@shared/helpers/save-debounce';
import { InvCountUacInfo } from 'projects/warehousing/src/app/models/inv-count-uac-info';
import { CustomValidators } from '@shared/helpers/custom-validators';
import { once } from '@shared/helpers/once';
import { SelectItem } from 'primeng/api';
import { InvCountProductHandling } from 'projects/warehousing/src/app/models/inv-count-product-handling';
import { ShiftContext } from 'projects/warehousing/src/app/models/shift-context';

type HandlingFormGroup = FormGroup<{
    id: FormControl<number>;
    displayOrder: FormControl<number>;
    productId: FormControl<number>;
    handledS1: FormControl<number>;
}>;

@Component({
    selector: 'app-uac-misc-info',
    templateUrl: './uac-misc-info.component.html',
    styleUrls: ['./uac-misc-info.component.scss']
})
export class UacMiscInfoComponent implements OnInit, OnDestroy {

    @Input() invCountId: number;
    @Input() set subinventoryId(subinventoryId: number) {
        this.siteSubinventoryId.next(subinventoryId);
    }

    context: ShiftContext;

    invCountUacInfo: InvCountUacInfo;

    sortActivityTypes: SortActivity[]= [];
    subscriptions: Subscription[];
    shutdown: Subject<void> = new Subject<void>();
    form = new FormGroup({
        trailersAtReturnCenter: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        trailersAtUac: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        trailersAtDrop: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        emptyTrailers: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),

        unlottedPallets: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        itCagePallets: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        itemResearchPallets: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),

        comments: new FormControl<string>(''),

        dropsUnloaded: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        liveLoadsUnloaded: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        palletPositionsOffloaded: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        outboundsShipped: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        propakScrapTrailerOutbounds: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        otherScrapTrailerOutbounds: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        scrapPreCrusherOutbounds: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        trashOutbounds: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        trashCompactorOutbounds: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ])
    })

    uacMiscInfoIsLoaded = false;
    uacMiscInfoIsSaved = false;

    handlingTypes: SelectItem[] = [];
    handlingItems: HandlingFormGroup[] = [];

    handlingAddForm: HandlingFormGroup = new FormGroup({
        id: new FormControl<number>(null),
        displayOrder: new FormControl<number>(null),
        productId: new FormControl<number>(null, Validators.required),
        handledS1: new FormControl<number>(0, [Validators.required, CustomValidators.numeric(false)]),
    });


    autoSaveFormSubscriptions: Subscription[];
    siteSubinventoryId = new BehaviorSubject<number>(null);

    constructor(
        private inventoryService: InventoryService,
        private productService: ProductService,
        private activityService: ActivityBlockingService,
        private shiftContextService: ShiftContextService,
        private messageService: MessageService) {
    }

    ngOnInit() {
        const validSortActivities = [
            SortActivity.PlusActivity,
            SortActivity.PremActivity,
            SortActivity.CustActivity,
            SortActivity.BlockRepairs
        ]
        const sortActivityTypesObservable = this.productService.SortActivityTypesForSubinventory(this.siteSubinventoryId)
            .pipe(tap(sortActivityTypes => {
                this.sortActivityTypes = sortActivityTypes.filter(x => validSortActivities.some(t => t === x));
            }));

        const contextObs = this.shiftContextService.CurrentContext.pipe(tap(context => {
            this.context = context;

            if (context.status !== ShiftStatus.Open) {
                this.form.disable({ emitEvent: false });
                this.handlingAddForm.disable({emitEvent: false});
                this.handlingItems.forEach(h => h.disable({emitEvent: false}));
            }
            else {
                this.form.enable({ emitEvent: false });
                this.handlingAddForm.enable({emitEvent: false});
                this.handlingItems.forEach(h => h.enable({emitEvent: false}));
            }
        }));

        const uacMiscInfoObservable = this.inventoryService.CurrentUacInfo.pipe(
            filter(info => !!info),
            tap(info => {
                this.invCountUacInfo = info;

                this.form.setValue({
                    trailersAtReturnCenter: info.trailersAtReturnCenter,
                    trailersAtUac: info.trailersAtUac,
                    trailersAtDrop: info.trailersAtDrop,
                    emptyTrailers: info.emptyTrailers,

                    unlottedPallets: info.unlottedPallets,
                    itCagePallets: info.itCagePallets,
                    itemResearchPallets: info.itemResearchPallets,
                    comments: info.comments,

                    dropsUnloaded: info.dropsUnloaded,
                    liveLoadsUnloaded: info.liveLoadsUnloaded,
                    palletPositionsOffloaded: info.palletPositionsOffloaded,
                    outboundsShipped: info.outboundsShipped,
                    propakScrapTrailerOutbounds: info.propakScrapTrailerOutbounds,
                    otherScrapTrailerOutbounds: info.otherScrapTrailerOutbounds,
                    scrapPreCrusherOutbounds: info.scrapPreCrusherOutbounds,
                    trashOutbounds: info.trashOutbounds,
                    trashCompactorOutbounds: info.trashCompactorOutbounds
                }, {
                    emitEvent: false
                });
            }));

        const handlingObservable = combineLatest([
            this.productService.ProductsForSubinventory(this.siteSubinventoryId),
            this.inventoryService.CurrentInvCountContext.pipe(filter(c => !!c))
        ]).pipe(
            tap(([products, _invCount]) => {
                this.handlingTypes = products
                    .filter(p => p.productType === 'Activity')
                    .map(p => ({ value: p.id, label: p.label }));
            }),
            once(([_products, invCount]) => {
                this.handlingItems = invCount.invCountProductHandling
                    .filter(h => this.handlingTypes.find(p => h.productId === p.value))
                    .map(h => this.createHandlingForm(h));
            }));

        this.subscriptions = [
            combineLatest([contextObs, sortActivityTypesObservable])
                .pipe(this.activityService.Wait('CHEP Misc Info'))
                .subscribe(),

            uacMiscInfoObservable.subscribe(),
            handlingObservable.subscribe(),

            this.form.valueChanges
                .pipe(saveDebounce(this.shutdown))
                .subscribe(_ => this.saveUacMiscInfo())
        ];
    }

    ngOnDestroy() {
        this.shutdown.next();
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    createHandlingForm(record: InvCountProductHandling): HandlingFormGroup {
        const form = new FormGroup({
            id: new FormControl<number>(record.id),
            displayOrder: new FormControl<number>(record.displayOrder ?? 0),
            productId: new FormControl<number>(record.productId, Validators.required),
            handledS1: new FormControl<number>(record.handledS1, [Validators.required, CustomValidators.numeric(false)]),
        });

        if (this.context?.status !== ShiftStatus.Open)
            form.disable();
        else
            form.enable();

        form.valueChanges
            .pipe(saveDebounce(this.shutdown))
            .subscribe(_ => {
                this.updateHandling(form);
            });

        return form;
    }

    addHandling(): void {
        if (!this.handlingAddForm.valid) {
            this.handlingAddForm.markAllAsTouched();
            this.messageService.add({
                severity: 'warn',
                summary: `Could not create Handling Line`,
                detail: 'Fields are required.'
            });
        } else {
            const entry: InvCountProductHandling = {
                invCountId: this.invCountId,
                displayOrder: this.handlingItems.length,
                productId: this.handlingAddForm.controls.productId.value,
                handledS1: this.handlingAddForm.controls.handledS1.value
            };

            this.inventoryService.createInvCountProductHandling(entry).subscribe(created => {
                this.handlingAddForm.reset({
                    productId: null,
                    handledS1: 0
                });
                this.messageService.add({
                    severity: 'success',
                    summary: `Handling Line Created`
                });

                this.handlingItems = [...this.handlingItems, this.createHandlingForm(created)];
            });
        }
    }

    updateHandling(form: HandlingFormGroup): void {
        const item: InvCountProductHandling = {
            invCountId: this.invCountId,
            displayOrder: form.controls.displayOrder.value,
            id: form.controls.id.value,
            productId: form.controls.productId.value,
            handledS1: form.controls.handledS1.value,
            handledS2: 0,
            handledS3: 0,
            handledS4: 0,
            handledS5: 0
        };
        this.inventoryService.updateInvCountProductHandling(item).subscribe(_updated => {
            this.messageService.add({
                severity: 'success',
                summary: 'Handling Item Updated',
                detail: ''
            });
        });
    }

    deleteHandling(item: HandlingFormGroup): void {
        this.inventoryService.deleteInvCountProductHandling(item.controls.id.value).subscribe(() => {
            this.handlingItems = this.handlingItems.filter(rsi => rsi.value.id !== item.value.id);

            this.messageService.add({
                severity: 'warn',
                summary: 'Item Deleted',
                detail: ''
            });
        });
    }

    saveUacMiscInfo() {
        const update: InvCountUacInfo = {
            ...this.invCountUacInfo,

            trailersAtReturnCenter: this.form.controls.trailersAtReturnCenter.value,
            trailersAtUac: this.form.controls.trailersAtUac.value,
            trailersAtDrop: this.form.controls.trailersAtDrop.value,
            emptyTrailers: this.form.controls.emptyTrailers.value,

            unlottedPallets: this.form.controls.unlottedPallets.value,
            itCagePallets: this.form.controls.itCagePallets.value,
            itemResearchPallets: this.form.controls.itemResearchPallets.value,

            comments: this.form.controls.comments.value ?? '',

            dropsUnloaded: this.form.controls.dropsUnloaded.value,
            liveLoadsUnloaded: this.form.controls.liveLoadsUnloaded.value,
            palletPositionsOffloaded: this.form.controls.palletPositionsOffloaded.value,
            outboundsShipped: this.form.controls.outboundsShipped.value,
            propakScrapTrailerOutbounds: this.form.controls.propakScrapTrailerOutbounds.value,
            otherScrapTrailerOutbounds: this.form.controls.otherScrapTrailerOutbounds.value,
            scrapPreCrusherOutbounds: this.form.controls.scrapPreCrusherOutbounds.value,
            trashOutbounds: this.form.controls.trashOutbounds.value,
            trashCompactorOutbounds: this.form.controls.trashCompactorOutbounds.value
        };

        if (!this.form.valid) {
            this.form.markAllAsTouched();
            this.messageService.add({
                severity: 'warn',
                summary: 'Could not Update Misc. Info',
                detail: 'Fields are required.'
            });
        } else {
            this.inventoryService.updateInvCountUacInfo(update).subscribe(_ => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Saved UAC Miscellaneous Info',
                    detail: ''
                });
            });
        }
    }

    rowTrackBy(_i: number, item: HandlingFormGroup): number {
        return item.controls.id.value;
    }
}
